<script setup lang="ts">
/**
 * Carte riche d'un objet (mode « grand ») — rendue dans le slot #content d'un
 * <AppInfoCard> (popover) ou inline via `<ItemDisplay size="large">`.
 *
 * Réutilise le langage visuel `.tt-*` (cf assets/styles/_tooltips.scss) :
 * en-tête icône+nom+code, rareté/kind, description, lignes d'effets (bonus +
 * affixes), méta. Quand un objet de RÉFÉRENCE est épinglé (store
 * itemComparison) et qu'il diffère de l'objet courant, chaque effet affiche
 * son delta (+/-) coloré selon le sens (higherIsBetter).
 */
import { computed, watch } from 'vue';
import { assetUrlFromCode } from '@/utils/assetUrl';
import { resourceColorByCode } from '@/utils/elementColor';
import { fuelTier, FUEL_TIER_COUNT, resourceRoleHint } from '@/utils/fuel';
import { useItemComparisonStore } from '@/stores/itemComparison.store';
import { useComponentSetStore } from '@/stores/componentSet.store';
import { useItemSetStore } from '@/stores/itemSet.store';
import { ITEM_KIND_LABELS } from '@/types/inventory';
import type { LibrarySetBonus } from '@/types/library';
import {
  type DisplayItem,
  type DisplayStat,
} from '@/types/itemDisplay';

const props = defineProps<{
  item: DisplayItem;
  /** Référence de comparaison EXPLICITE (ex. composant équipé sur la case du
   *  studio, survolé dans la palette). Prioritaire sur l'épinglage global ;
   *  ignorée si c'est le même objet. null/absent = retombe sur le store. */
  reference?: DisplayItem | null;
}>();

const cmp = useItemComparisonStore();

// Set : encart « ce que le set apporte » (paliers de bonus). Le setId peut désigner
// un set de COMPOSANTS (vaisseau) ou un set d'OBJETS (reliques, bonus joueur au musée).
// Les deux référentiels n'ont pas d'id en commun -> on interroge les deux stores.
const setStore = useComponentSetStore();
const itemSetStore = useItemSetStore();
const setInfo = computed(() => setStore.get(props.item.setId) ?? itemSetStore.get(props.item.setId));
watch(
  () => props.item.setId,
  (id) => { if (id != null) { void setStore.ensureLoaded(); void itemSetStore.ensureLoaded(); } },
  { immediate: true },
);
/** « +12 Attaque » / « +5% Vitesse de fouille » (signe selon la valeur). */
function setBonusText(b: LibrarySetBonus): string {
  const sign = b.value < 0 ? '' : '+';
  const suffix = b.mode === 'percent' ? '%' : '';
  return `${sign}${b.value}${suffix} ${b.attributeLabel}`;
}

function imgUrl(it: DisplayItem): string | null {
  const code = it.imageCode ?? it.code;
  return code ? assetUrlFromCode(it.imageFolder ?? 'items', code) : null;
}

// Indications COMPOSANT : taille (hexagones colorés par la ressource) + ressource.
const hexColor = computed(() => resourceColorByCode(props.item.fuelCode));
// Ressource du composant : carburant (moteur/gén./réservoir) avec un RANG (1..4 :
// plus avancé = + de portée/énergie) ou élément de combat (arme/bouclier, sans rang).
const fuelRank = computed(() => fuelTier(props.item.fuelCode));
const resHint = computed(() => resourceRoleHint(props.item.fuelCode));
function resImg(code: string): string {
  return assetUrlFromCode('resources', code);
}

/** Référence de comparaison active, sauf si c'est l'objet lui-même. Référence
 *  explicite (prop) prioritaire ; sinon l'objet épinglé globalement (store). */
const reference = computed<DisplayItem | null>(() => {
  const explicit = props.reference;
  if (explicit && explicit.id !== props.item.id) return explicit;
  const p = cmp.pinned;
  return p && p.id !== props.item.id ? p : null;
});

const comparing = computed(() => reference.value !== null);

interface CmpRow {
  key: string;
  label: string;
  isPercent: boolean;
  /** Valeur sur l'objet courant (null = absent). */
  value: number | null;
  /** Valeur sur la référence (null = absent). */
  refValue: number | null;
  /** Delta courant - réf. */
  delta: number;
  /** true = mieux, false = moins bien, null = égal/non comparé. */
  better: boolean | null;
  /** Stat de BASE (caractéristique figée) vs affixe bonus/malus. */
  isPrimary: boolean;
  /** Sens favorable : true = plus c'est haut, mieux c'est ; false = plus c'est
   *  bas, mieux c'est (conso, coût…). Pilote le SENS de la barre + bonus/malus. */
  higherIsBetter: boolean;
  /** Bornes de la plage de roll (null = inconnue → pas de barre). */
  min: number | null;
  max: number | null;
}

function fmt(stat: { key: string; isPercent: boolean }, value: number): string {
  const sign = value > 0 ? '+' : '';
  return stat.isPercent ? `${sign}${value}%` : `${sign}${value}`;
}

/** Stat de BASE : valeur brute SANS signe « + » (ce n'est pas un bonus). */
function fmtBase(stat: { isPercent: boolean }, value: number): string {
  return stat.isPercent ? `${value}%` : `${value}`;
}

/** La ligne porte-t-elle une plage exploitable pour une barre ? */
function hasRange(r: CmpRow): boolean {
  return r.value !== null && r.min !== null && r.max !== null && r.max > r.min;
}

/** QUALITÉ du roll dans sa plage [min..max], en % (0..100, clampé) : 100 % = le
 *  MEILLEUR roll possible, 0 % = le pire. Tient compte du SENS (higherIsBetter) :
 *  pour une stat « plus bas = mieux » (conso, coût), une petite valeur donne une
 *  barre PLEINE (sinon un bon roll bas affichait une barre vide → trompeur). */
function fillPct(r: CmpRow): number {
  if (!hasRange(r)) return 0;
  const pos = (r.value! - r.min!) / (r.max! - r.min!);     // position brute (min=0)
  const quality = r.higherIsBetter ? pos : 1 - pos;         // inversée si bas=mieux
  return Math.round(Math.min(1, Math.max(0, quality)) * 100);
}

/** Malus = l'effet va dans le MAUVAIS sens pour le joueur (≠ simple signe « − » :
 *  un « −5 % conso » est un BONUS). Sert à colorer le nom en rouge. */
function isMalus(r: CmpRow): boolean {
  if (r.value === null || r.value === 0) return false;
  return r.higherIsBetter ? r.value < 0 : r.value > 0;
}
function isBonus(r: CmpRow): boolean {
  if (r.value === null || r.value === 0) return false;
  return r.higherIsBetter ? r.value > 0 : r.value < 0;
}

/** Lignes fusionnées courant ∪ référence (par clé), triées primaire d'abord. */
const rows = computed<CmpRow[]>(() => {
  const ref = reference.value;
  const byKey = new Map<string, { cur?: DisplayStat; ref?: DisplayStat }>();
  for (const s of props.item.stats) byKey.set(s.key, { cur: s });
  if (ref) {
    for (const s of ref.stats) {
      const e = byKey.get(s.key) ?? {};
      e.ref = s;
      byKey.set(s.key, e);
    }
  }
  const out: CmpRow[] = [];
  for (const [key, e] of byKey) {
    const meta = e.cur ?? e.ref!;
    const value = e.cur ? e.cur.value : null;
    const refValue = e.ref ? e.ref.value : null;
    const delta = (value ?? 0) - (refValue ?? 0);
    let better: boolean | null = null;
    if (ref && delta !== 0) better = meta.higherIsBetter ? delta > 0 : delta < 0;
    out.push({ key, label: meta.label, isPercent: meta.isPercent, value, refValue, delta, better, isPrimary: meta.isPrimary, higherIsBetter: meta.higherIsBetter, min: meta.min ?? null, max: meta.max ?? null });
  }
  // Primaire d'abord, puis alpha.
  return out.sort((a, b) => (a.isPrimary === b.isPrimary ? a.label.localeCompare(b.label) : (a.isPrimary ? -1 : 1)));
});

/** Stats de BASE (caractéristiques du composant) vs ATTRIBUTS bonus/malus (affixes). */
const baseRows = computed<CmpRow[]>(() => rows.value.filter((r) => r.isPrimary));
const affixRows = computed<CmpRow[]>(() => rows.value.filter((r) => !r.isPrimary));

/** Couleur de la valeur d'affixe = SENS de l'effet (bonus vert / malus rouge),
 *  pas le signe brut : « −5 % conso » est un bonus (vert), « +20 % conso » un malus. */
function rowClass(r: CmpRow): string {
  if (isBonus(r)) return 'is-pos';
  if (isMalus(r)) return 'is-neg';
  return '';
}

function deltaClass(r: CmpRow): string {
  if (r.better === true) return 'is-pos';
  if (r.better === false) return 'is-neg';
  return '';
}

function deltaText(r: CmpRow): string {
  if (r.delta === 0) return '=';
  const arrow = r.better === true ? '▲' : r.better === false ? '▼' : '';
  const sign = r.delta > 0 ? '+' : '';
  const val = r.isPercent ? `${sign}${r.delta}%` : `${sign}${r.delta}`;
  return `${arrow} ${val}`.trim();
}

function formatAcquired(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString();
}

// Ligne de TYPE (façon carte MTG) : catégorie (Kind) + type précis.
const kindLabel = computed(() => (props.item.kind ? ITEM_KIND_LABELS[props.item.kind] : ''));
// Type précis affiché UNIQUEMENT s'il n'est pas un doublon de la catégorie
// (les artefacts/reliques ont typeLabel === kindLabel -> on évite « Artefact · Artefact »).
const showTypeLabel = computed(
  () => !!props.item.typeLabel && props.item.typeLabel !== kindLabel.value,
);
const showTypeLine = computed(
  () => !!(kindLabel.value || showTypeLabel.value || props.item.fuelCode || props.item.isBasic),
);
// Flavor / description : affichée sous la ligne de type. On évite le doublon quand
// la description n'est que le libellé de type (cas de certains composants).
const showFlavor = computed(
  () => !!props.item.description && props.item.description !== props.item.typeLabel,
);
// Taille (analogue au « coût » MTG, en haut à droite) : hexagones colorés par la
// ressource. Présente pour les composants ; prête pour artefacts/reliques (Lot 2).
const slotSize = computed(() => props.item.slotSize ?? 0);
</script>

<template>
  <div class="tt-card item-tt" :class="[`rarity-${item.rarity}`, { 'is-set': item.setId != null }]">
    <!-- 1. BANDEAU TITRE (MTG) : nom coloré par la rareté à gauche, TAILLE
         (analogue au coût d'une carte) en hexagones colorés par la ressource à droite. -->
    <div class="itt-title">
      <span class="itt-name">{{ item.name }}</span>
      <span
        v-if="slotSize > 0"
        class="itt-size"
        :title="`Taille : ${slotSize}/4`"
      >
        <span
          v-for="n in 4"
          :key="n"
          class="tt-hex"
          :class="{ on: n <= slotSize }"
          :style="n <= slotSize ? { color: hexColor } : undefined"
        >⬢</span>
      </span>
    </div>

    <!-- Bandeau comparaison (objet de référence épinglé). -->
    <div v-if="comparing" class="itt-cmp">
      <span class="itt-cmp-lbl">Comparé à</span>
      <span class="itt-cmp-ref">{{ reference!.name }}</span>
    </div>

    <!-- 2. CORPS (Diablo) : image à gauche, caractéristiques de BASE à droite
         (la plus importante en gros, sans barre de qualité pour gagner de la place). -->
    <div class="itt-body">
      <div class="itt-art">
        <img v-if="imgUrl(item)" :src="imgUrl(item)!" :alt="item.name" class="itt-art-img" v-img-hide-on-error />
        <span v-if="item.quantity && item.quantity > 1" class="itt-qty text-mono">×{{ item.quantity }}</span>
        <span v-if="item.augmentationLevel && item.augmentationLevel > 0" class="itt-aug" :title="`Augmentation +${item.augmentationLevel}`">★{{ item.augmentationLevel }}</span>
      </div>

      <ul v-if="baseRows.length" class="itt-basestats">
        <li v-for="(r, i) in baseRows" :key="r.key" class="itt-bs" :class="{ 'is-master': i === 0 }">
          <span class="itt-bs-label">{{ r.label }}</span>
          <span class="itt-bs-vals">
            <span class="itt-bs-val">{{ r.value !== null ? fmtBase(r, r.value) : '—' }}</span>
            <span v-if="comparing" class="item-tt-delta" :class="deltaClass(r)">{{ deltaText(r) }}</span>
          </span>
        </li>
      </ul>
      <!-- Pas de stats de base (relique, artefact, document…) : on met le texte
           d'ambiance à côté de l'image (image + lore, façon Diablo). -->
      <p v-else-if="showFlavor" class="itt-flavor itt-flavor--inbody">{{ item.description }}</p>
      <span v-else class="itt-nostat">—</span>
    </div>

    <!-- 3. LIGNE DE TYPE (MTG) : type à gauche, ressource utilisée (image + nom) à droite. -->
    <div v-if="showTypeLine" class="itt-typeline">
      <span class="itt-type">
        <span v-if="item.isBasic" class="itt-basic" title="Composant basique — équipement de départ (stats au plancher de qualité, non looté)">🔧</span>
        <span v-if="kindLabel" class="itt-kind">{{ kindLabel }}</span>
        <template v-if="showTypeLabel"><span v-if="kindLabel" class="itt-type-sep">·</span>{{ item.typeLabel }}</template>
      </span>
      <span v-if="item.fuelCode" class="itt-res" :title="resHint">
        <img :src="resImg(item.fuelCode)" class="itt-res-img" :alt="item.fuelLabel ?? ''" v-img-hide-on-error />
        <span class="itt-res-name">{{ item.fuelLabel }}</span>
        <span v-if="fuelRank" class="itt-fuel-tier">⛽ {{ fuelRank }}/{{ FUEL_TIER_COUNT }}</span>
      </span>
    </div>

    <!-- 4. DESCRIPTION / FLAVOR (texte d'ambiance, teinté par la rareté).
         Masquée ici si déjà affichée à côté de l'image (items sans stats de base). -->
    <p v-if="showFlavor && baseRows.length" class="itt-flavor">{{ item.description }}</p>

    <!-- Encart SET : ce que le set apporte (paliers de bonus par nb de pièces). -->
    <div v-if="setInfo" class="item-tt-set">
      <span class="item-tt-set-title">★ Set : {{ setInfo.label }}</span>
      <p v-if="setInfo.description" class="item-tt-set-desc">{{ setInfo.description }}</p>
      <ul v-if="setInfo.bonuses.length" class="item-tt-set-bonuses">
        <li v-for="b in setInfo.bonuses" :key="`${b.piecesRequired}-${b.attributeKey}`">
          <span class="item-tt-set-pc">{{ b.piecesRequired }} pièces</span>
          <span class="item-tt-set-bn">{{ setBonusText(b) }}</span>
        </li>
      </ul>
      <p v-else class="item-tt-set-desc">Porter plusieurs pièces de ce set octroie des bonus au vaisseau.</p>
    </div>

    <!-- 5. ATTRIBUTS (affixes bonus/malus) : barre de qualité CONSERVÉE ici. -->
    <template v-if="affixRows.length">
      <div class="itt-attr-head"><span class="itt-attr-title">Attributs</span></div>
      <ul class="tt-rows">
        <li v-for="r in affixRows" :key="r.key" class="tt-row">
          <span class="tt-label" :class="{ 'is-malus': isMalus(r), 'is-bonus': isBonus(r) }">{{ r.label }}</span>
          <span
            class="tt-bar"
            :class="{ 'is-empty': !hasRange(r) }"
            :title="hasRange(r) ? `qualité du roll : ${fillPct(r)} % (plage ${r.min}–${r.max})` : undefined"
          >
            <span v-if="hasRange(r)" class="tt-bar-fill" :style="{ width: fillPct(r) + '%' }" />
          </span>
          <span class="item-tt-vals">
            <span class="tt-value" :class="rowClass(r)">{{ r.value !== null ? fmt(r, r.value) : '—' }}</span>
            <span v-if="comparing" class="item-tt-delta" :class="deltaClass(r)">{{ deltaText(r) }}</span>
          </span>
        </li>
      </ul>
    </template>
    <p v-else-if="!baseRows.length && !showFlavor" class="itt-empty">Aucun effet particulier.</p>

    <!-- 6. FOOTER : provenance (source + date) à gauche, échangeabilité à droite. -->
    <div v-if="item.origin || item.acquiredAt || item.isTradable != null" class="itt-footer">
      <span v-if="item.origin || item.acquiredAt" class="itt-source">
        <template v-if="item.origin">{{ item.origin }}</template>
        <template v-if="item.acquiredAt"><span v-if="item.origin"> · </span>{{ formatAcquired(item.acquiredAt) }}</template>
      </span>
      <span
        v-if="item.isTradable != null"
        class="itt-trade"
        :class="item.isTradable ? 'is-yes' : 'is-no'"
      >{{ item.isTradable ? '✔ Échangeable' : '✖ Non échangeable' }}</span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// =============================================================================
// Carte objet — cadre « MTG » (bandeau titre / art / ligne de type / texte) x
// tooltip « Diablo 3 » (rareté = couleur, stat maîtresse mise en avant, flavor).
// La carte porte ELLE-MÊME le cadre de rareté et remplit le popover bord à bord
// (cf .item-tt-popover dans _tooltips.scss : padding/fond/bordure neutralisés).
// =============================================================================
.item-tt {
  gap: 0;                     // bandeaux/sections FLUSH (look « carte », cf .tt-card)
  border: 1px solid transparent;
  border-radius: $radius-md;
  padding: 0;                 // les sections gèrent leur propre padding
  overflow: hidden;           // le contenu ne déborde pas du cadre arrondi
  // Filet de sécurité hauteur (un objet très riche défile plutôt que déborder l'écran).
  max-height: 86vh;
  overflow-y: auto;

  // Rareté = contour + fond dégradé (échelle 0..3 = Commun / Rare / Épique / Légendaire).
  &.rarity-0 {
    border-color: $rarity-common;
    background: linear-gradient(165deg, rgba($rarity-common, 0.18), rgba($rarity-common, 0.05) 60%), $bg-elevated;
  }
  &.rarity-1 {
    border-color: $rarity-rare;
    background: linear-gradient(165deg, rgba($rarity-rare, 0.30), rgba($rarity-rare, 0.08) 60%), $bg-elevated;
  }
  &.rarity-2 {
    border-color: $rarity-epic;
    background: linear-gradient(165deg, rgba($rarity-epic, 0.34), rgba($rarity-epic, 0.09) 60%), $bg-elevated;
  }
  &.rarity-3 {
    border-color: $rarity-legendary;
    background: linear-gradient(165deg, rgba($rarity-legendary, 0.38), rgba($rarity-legendary, 0.10) 60%), $bg-elevated;
  }
  // Set : vert prioritaire sur la rareté.
  &.is-set {
    border-color: $rarity-set;
    background: linear-gradient(165deg, rgba($rarity-set, 0.32), rgba($rarity-set, 0.09) 60%), $bg-elevated;
  }
}

$itt-pad: $space-3;

// ---- 1. Bandeau titre -----------------------------------------------------
.itt-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-2;
  padding: $space-2 $itt-pad;
  background: rgba(0, 0, 0, 0.28);              // bandeau assombri (lisibilité du nom)
  border-bottom: 1px solid rgba(255, 255, 255, 0.10);
}
.itt-name {
  font-size: $font-size-md;
  font-weight: $font-weight-bold;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
// Nom coloré par la rareté (réflexe Diablo, en plus du cadre).
.rarity-0 .itt-name { color: $rarity-common; }
.rarity-1 .itt-name { color: $rarity-rare; }
.rarity-2 .itt-name { color: $rarity-epic; }
.rarity-3 .itt-name { color: $rarity-legendary; }
.is-set .itt-name { color: $rarity-set; }

// Taille (coût MTG) : hexagones colorés par la ressource.
.itt-size {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
}
.tt-hex {
  font-size: 13px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.18);           // taille non atteinte
  &.on { color: $color-neon-cyan; }           // atteinte (fallback si pas de ressource)
}

// ---- Bandeau comparaison --------------------------------------------------
.itt-cmp {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 2px $itt-pad 0;
}
.itt-cmp-lbl {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: $text-muted;
}
.itt-cmp-ref {
  font-size: 11px;
  color: $accent-primary;
  font-weight: $font-weight-medium;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// ---- 2. Corps : art + stats de base ---------------------------------------
.itt-body {
  display: flex;
  gap: $space-3;
  padding: $space-2 $itt-pad;
}
.itt-art {
  position: relative;
  flex-shrink: 0;
  width: 88px;
  height: 88px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: $radius-sm;
  overflow: hidden;
}
.itt-art-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.itt-qty {
  position: absolute;
  bottom: 2px;
  right: 3px;
  padding: 0 5px;
  font-size: $font-size-xs;
  color: $text-primary;
  background: rgba(0, 0, 0, 0.78);
  border-radius: $radius-sm;
}
.itt-aug {
  position: absolute;
  top: 2px;
  left: 2px;
  padding: 0 4px;
  font-size: 10px;
  font-weight: $font-weight-bold;
  color: #1b1407;
  background: $rarity-legendary;
  border-radius: $radius-sm;
}

// Caractéristiques de BASE : liste sobre (label | valeur), la première en gros.
.itt-basestats {
  flex: 1 1 auto;
  min-width: 0;
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-self: center;
}
.itt-bs {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: $space-2;
}
.itt-bs-label {
  font-size: $font-size-xs;
  color: $text-secondary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.itt-bs-vals {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-shrink: 0;
}
.itt-bs-val {
  font-family: $font-mono;
  font-size: $font-size-sm;
  color: $text-primary;
}
// Stat maîtresse : nettement plus grosse (accent Diablo).
.itt-bs.is-master {
  .itt-bs-label { font-size: $font-size-sm; color: $text-primary; }
  .itt-bs-val {
    font-size: $font-size-xl;
    font-weight: $font-weight-bold;
    color: $color-neon-cyan;
    line-height: 1.1;
  }
}
.itt-nostat {
  flex: 1 1 auto;
  align-self: center;
  margin: 0;
  font-size: $font-size-xs;
  color: $text-muted;
  font-style: italic;
}

// ---- 3. Ligne de type + ressource -----------------------------------------
.itt-typeline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  padding: 5px $itt-pad;
  background: rgba(0, 0, 0, 0.22);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.itt-type {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  font-size: $font-size-sm;
  font-weight: $font-weight-medium;
  color: $text-secondary;
}
.itt-basic {
  font-size: 12px;
  line-height: 1;
}
.itt-kind { color: $text-primary; }
.itt-type-sep { color: $text-muted; }
.itt-res {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.itt-res-img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  border-radius: 3px;
}
.itt-res-name {
  font-size: 11px;
  color: $text-muted;
}
.itt-fuel-tier {
  font-size: 10px;
  font-family: $font-mono;
  color: $color-warning;
  padding: 0 4px;
  border-radius: $radius-sm;
  background: rgba($color-warning, 0.12);
  white-space: nowrap;
}

// ---- 4. Flavor / description -----------------------------------------------
.itt-flavor {
  margin: 0;
  padding: $space-2 $itt-pad 0;
  font-size: 11.5px;
  font-style: italic;
  line-height: 1.45;
  color: $text-secondary;
}
// Teinte du flavor par rareté (subtile), façon texte légendaire.
.rarity-2 .itt-flavor { color: rgba($rarity-epic, 0.92); }
.rarity-3 .itt-flavor { color: rgba($rarity-legendary, 0.92); }
// Flavor affiché À CÔTÉ de l'image (items sans stats de base) : centré, tronqué.
.itt-flavor--inbody {
  align-self: center;
  flex: 1 1 auto;
  min-width: 0;
  padding: 0;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

// ---- Encart SET ------------------------------------------------------------
.item-tt-set {
  margin: $space-2 $itt-pad 0;
  padding: 6px 8px;
  border: 1px solid rgba($rarity-set, 0.7);
  border-radius: 6px;
  background: rgba(4, 14, 10, 0.62);
}
.item-tt-set-title { font-size: 12px; font-weight: 700; color: $color-success; }
.item-tt-set-desc {
  margin: 2px 0 4px;
  font-size: 11px;
  color: $text-primary;
  line-height: 1.4;
}
.item-tt-set-bonuses {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.item-tt-set-bonuses li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
}
.item-tt-set-pc { color: $text-secondary; }
.item-tt-set-bn { color: $color-success; font-family: $font-mono; }

// ---- 5. Attributs (affixes) : barre de qualité conservée ------------------
.itt-attr-head {
  padding: $space-2 $itt-pad 2px;
}
.itt-attr-title {
  font-size: 10px;
  font-weight: $font-weight-bold;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $text-muted;
}
// Colonnes : LIBELLÉ (extensible, s'enroule sur 2 lignes si long) | BARRE (largeur
// FIXE -> toujours visible, jamais écrasée par un libellé long) | VALEUR (auto).
.tt-rows {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 58px max-content;
  align-items: center;
  column-gap: $space-2;
  row-gap: 5px;
  padding: 0 $itt-pad $space-1;
}
.tt-row { display: contents; }

.item-tt-vals {
  display: inline-flex;
  align-items: baseline;
  justify-self: end;
  gap: 6px;
}
.tt-bar {
  min-width: 36px;
  height: 6px;
  border-radius: $radius-full;
  background: rgba(255, 255, 255, 0.1);
  overflow: hidden;
  &.is-empty { background: transparent; }
}
.tt-bar-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, $color-neon-cyan, $accent-primary);
}
.item-tt-delta {
  font-family: $font-mono;
  font-size: 11px;
  color: $text-muted;
  &.is-pos { color: $color-success; }
  &.is-neg { color: $color-danger; }
}
// Attribut coloré selon le SENS : MALUS rouge, BONUS cyan. Le libellé peut
// s'enrouler sur 2 lignes (colonne bornée) plutôt que d'écraser la barre.
.tt-label {
  font-size: $font-size-sm;
  line-height: 1.2;
  overflow-wrap: anywhere;
  &.is-malus { color: $color-danger; font-weight: 600; }
  &.is-bonus { color: $color-neon-cyan; }
}
.tt-value {
  font-family: $font-mono;
  font-size: $font-size-sm;
  &.is-pos { color: $color-neon-cyan; }
  &.is-neg { color: $color-danger; }
}
.itt-empty {
  margin: 0;
  padding: $space-2 $itt-pad;
  font-size: 11px;
  font-style: italic;
  color: $text-muted;
}

// ---- 6. Footer -------------------------------------------------------------
.itt-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-2;
  flex-wrap: wrap;
  margin-top: 2px;
  padding: 6px $itt-pad;
  background: rgba(0, 0, 0, 0.28);
  border-top: 1px solid rgba(255, 255, 255, 0.10);
}
.itt-source {
  font-size: 10.5px;
  color: $text-muted;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.itt-trade {
  flex-shrink: 0;
  padding: 1px 8px;
  border-radius: $radius-full;
  font-size: 11px;
  font-weight: $font-weight-bold;
  border: 1px solid currentColor;
  &.is-yes { color: $color-success; background: rgba($color-success, 0.12); }
  &.is-no { color: $color-warning; background: rgba($color-warning, 0.12); }
}
</style>
